import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PictionJS from 'piction-js';
import { Dropdown } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import UMODetailViewRelated from './UMODetailViewRelated';
import UMODetailViewMeta from './UMODetailViewMeta';
import UMOMediaView from '../UMOMediaView';
import { isEditMode } from '../../utils/piction';
import appConfig from '../../appConfig';
import ErrorHandler from '../ErrorHandler';
import UMODownloadView from '../UMODownloadView';

function UMODetailViewBody({
  dispatch,
  umo,
  alternates,
  onAltClick,
  primaryID,
  onPrimarySelect,
  onFullImageToggle,
  toggleUMODetailView,
  selectedWorkspaceData,
  isWorkspace,
  workspaceID,
  selectedDataLanguage,
  languageList,
  changeDataLanguage,
  intl,
}) {
  const isRestricted =
    umo.thumbnail &&
    umo.thumbnail._data &&
    umo.thumbnail._data.$.restricted === 'T';
  const [isMediaObj, setIsMediaObj] = useState(false);
  const fileExtension = umo.name.split('.').pop().toLowerCase();
  let setPrimary = '';
  if (alternates && isEditMode()) {
    if (primaryID === umo.id) {
      setPrimary = (
        <span
          className="umo-detail-view__image-display-set-primary-button-full"
          data-toggle="tooltip"
          data-placement="bottom"
          title={intl.formatMessage({ id: 'umoDetailView.primaryImageLabel' })}
        >
          P
        </span>
      );
    } else {
      setPrimary = (
        <span
          onClick={() => onPrimarySelect(umo.id)}
          className="umo-detail-view__image-display-set-primary-button btn-link"
          data-toggle="tooltip"
          data-placement="bottom"
          title={intl.formatMessage({ id: 'umoDetailView.setAsPrimaryButton' })}
        >
          P
        </span>
      );
    }
  }

  const titleMetatag =
    appConfig.umoTitleMetatag &&
    umo.metadataList.getMetadata(appConfig.umoTitleMetatag) &&
    umo.metadataList.getMetadata(appConfig.umoTitleMetatag).value;
  const title = titleMetatag || umo.name;

  let fullImageFromTagValue = true;
  if (
    appConfig.advanced &&
    appConfig.advanced.zoomifyMetatag &&
    appConfig.advanced.zoomifyMetatag.tagName &&
    appConfig.advanced.zoomifyMetatag.tagValue
  ) {
    const configTag = appConfig.advanced.zoomifyMetatag;
    const umoTag =
      umo.metadataList.getMetadata(configTag.tagName.toUpperCase()) &&
      umo.metadataList.getMetadata(configTag.tagName.toUpperCase()).value;
    const tagValues = Array.isArray(configTag.tagValue)
      ? configTag.tagValue.map((val) => val.toUpperCase())
      : [configTag.tagValue.toUpperCase()];

    fullImageFromTagValue = umoTag && tagValues.includes(umoTag.toUpperCase());
  }

  // Allow fullscreen view for photos (web quality) or documents/records (html view)
  const fullImageType =
    (umo.type === 'PHOTO' && umo.webQuality) ||
    (!['PHOTO', 'AUDIO', 'VIDEO'].includes(umo.type) &&
      umo.originalList.all[0] &&
      umo.originalList.all[0]._data.$.h);

  return (
    <div className="umo-detail-view">
      <div className="umo-detail-view__media-container">
      <div className="umo-detail-view__image">
        {appConfig.umoDisplayTitleAboveImage ? (
          <div className="umo-detail-view__image-header umo-detail-view__image-header-title">
            {title}
          </div>
        ) : null}
        <div className="umo-detail-view__image-display">
          <div className="umo-detail-view__media">
            <ErrorHandler>
              <UMOMediaView
                umo={umo}
                isMediaObj={isMediaObj}
                setIsMediaObj={setIsMediaObj}
              />
            </ErrorHandler>
          </div>
          <ErrorHandler>
            {fullImageFromTagValue &&
            !isRestricted &&
            fullImageType &&
            !isMediaObj &&
            !appConfig.umoGallery?.filesWithNoPreview?.some(
              (file) => file.toLowerCase() === fileExtension
            ) ? (
              <div>
                <span
                  onClick={onFullImageToggle}
                  className="umo-detail-view__image-display-fullscreen-button"
                />
              </div>
            ) : null}
          </ErrorHandler>
          {setPrimary}
          <Dropdown
            id="dropdown-custom-2"
            className="umo-detail-view__image-display-download-image-button"
            variant="default"
            data-toggle="tooltip"
            data-placement="bottom"
          >
            <Dropdown.Toggle
              variant="secondary"
              size="sm"
              disabled={
                isRestricted ||
                (umo.originalList &&
                  umo.originalList.all &&
                  umo.originalList.all.length === 0)
              }
            >
              <i className="umo-detail-view__image-display-download-image-toggle" />
              <i className="fas fa-caret-down" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <UMODownloadView
                umo={umo}
                isRestricted={isRestricted}
                dispatch={dispatch}
              />
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      {alternates ? (
        <UMODetailViewRelated
          umo={umo}
          alternates={alternates}
          onAltClick={onAltClick}
        />
      ) : null}
      </div>

      <UMODetailViewMeta
        umo={umo}
        title={appConfig.umoDisplayTitleAboveImage ? null : title}
        toggleUMODetailView={toggleUMODetailView}
        selectedWorkspaceData={selectedWorkspaceData}
        dispatch={dispatch}
        isWorkspace={isWorkspace}
        workspaceID={workspaceID}
        selectedDataLanguage={selectedDataLanguage}
        languageList={languageList}
        changeDataLanguage={changeDataLanguage}
      />
    </div>
  );
}

UMODetailViewBody.propTypes = {
  umo: PropTypes.instanceOf(PictionJS.UMO),
  alternates: PropTypes.instanceOf(PictionJS.UMOList),
  onAltClick: PropTypes.func,
  primaryID: PropTypes.string,
  onPrimarySelect: PropTypes.func,
  onFullImageToggle: PropTypes.func,
  toggleUMODetailView: PropTypes.func,
  dispatch: PropTypes.func,
  selectedWorkspaceData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  isWorkspace: PropTypes.bool,
  workspaceID: PropTypes.string,
  selectedDataLanguage: PropTypes.object,
  languageList: PropTypes.array,
  changeDataLanguage: PropTypes.func,
};

export default injectIntl(UMODetailViewBody);
